import { avisarVencida, leerSesion } from "./sesion";
import type {
  Catalogo,
  Contacto,
  Historial,
  PerfilVendedor,
  ResultadoCalculo,
  Vendedor,
} from "../domain/types";
import { registro } from "../domain/contracts";
import type { DatosPerfil } from "../domain/perfil";
import type { OrdenRegistrada } from "../domain/ordenProduccion";
export const API_BASE = (
  import.meta.env.VITE_API_BASE ||
  "https://cotizador-invers-n8n.0h6lxz.easypanel.host/webhook"
).replace(/\/$/, "");
// Lista de contactos del CRM (GHL de Invers, cuenta de Diez Visual). Para
// cuando el contacto todavía no existe: el vendedor lo crea allí y vuelve.
export const CRM_CONTACTOS_URL =
  import.meta.env.VITE_CRM_CONTACTOS_URL ||
  "https://app.inverstudio.com/v2/location/iBxwjrP7hX8ojYqAOiYc/contacts/smart_list/All";
export const EVENTS_URL =
  import.meta.env.VITE_CATALOG_EVENTS_URL ??
  "https://cotizador-dv-backend.ocbtxi.easypanel.host/eventos/catalogo";
function hayVendedor() {
  try {
    return Boolean(localStorage.getItem("cotizador_vendedor"));
  } catch {
    return false;
  }
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}
// Las únicas llamadas que se hacen sin sesión: listar vendedores para el
// selector y validar el PIN (que es lo que entrega la sesión).
const SIN_SESION = new Set(["vendedores", "vendedores/validar"]);

export async function request(
  path: string,
  options: RequestInit = {},
  timeout = 20000,
): Promise<Response> {
  const sesion = leerSesion();
  if (!sesion && !SIN_SESION.has(path.split("?")[0]) && hayVendedor()) {
    // Venció mientras trabajaba: volver al login antes de que el backend
    // responda con un error que el vendedor no entendería.
    avisarVencida();
    throw new ApiError(
      "Tu sesión venció. Vuelve a ingresar con tu PIN; lo que no guardaste sigue en pantalla.",
      401,
    );
  }
  const signal = options.signal
    ? AbortSignal.any([options.signal, AbortSignal.timeout(timeout)])
    : AbortSignal.timeout(timeout);
  const response = await fetch(`${API_BASE}/${path}`, {
    ...options,
    signal,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(sesion ? { "x-sesion": sesion } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    // El backend rechazó la sesión (PIN cambiado, vendedor desactivado).
    if (response.status === 401 && body?.codigo === "sesion_invalida")
      avisarVencida();
    throw new ApiError(
      typeof body?.error === "string"
        ? body.error
        : `El servicio respondió con un error (${response.status}).`,
      response.status,
    );
  }
  return response;
}
export async function json<T>(
  path: string,
  options: RequestInit = {},
  timeout?: number,
): Promise<T> {
  return (await request(path, options, timeout)).json() as Promise<T>;
}
export const post = <T>(path: string, body: unknown, timeout?: number) =>
  json<T>(path, { method: "POST", body: JSON.stringify(body) }, timeout);
export function query(
  path: string,
  params: Record<string, string | undefined>,
) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v) q.set(k, v);
  });
  return `${path}?${q}`;
}
export async function catalogo(signal?: AbortSignal): Promise<Catalogo> {
  const value = registro(await json<unknown>("cotizador-datos", { signal }));
  for (const key of ["catalogo", "ciudades", "addons"])
    if (!Array.isArray(value[key]) || !(value[key] as unknown[]).length)
      throw new Error(
        "El catálogo no está disponible. Reintenta antes de cotizar.",
      );
  for (const p of value.catalogo as unknown[]) {
    const r = registro(p);
    if (
      typeof r.familia !== "string" ||
      typeof r.variante !== "string" ||
      !["m2", "cm2", "ml", "cm_lineal", "unidad"].includes(String(r.unidad)) ||
      (r.precio !== null &&
        (typeof r.precio !== "number" ||
          !Number.isFinite(r.precio) ||
          r.precio < 0))
    )
      throw new Error(
        "El catálogo contiene un precio, unidad o producto inválido.",
      );
  }
  return {
    ...value,
    preciosContrato: Array.isArray(value.preciosContrato)
      ? value.preciosContrato
      : [],
    catalogoClienteEspecial: Array.isArray(value.catalogoClienteEspecial)
      ? value.catalogoClienteEspecial
      : [],
    materiales: Array.isArray(value.materiales) ? value.materiales : [],
  } as unknown as Catalogo;
}
export async function vendedores(signal?: AbortSignal) {
  const d = await json<Vendedor[]>("vendedores", { signal });
  if (!Array.isArray(d))
    throw new Error("No se pudo cargar la lista de vendedores.");
  return d;
}
export async function perfil(id: string, signal?: AbortSignal) {
  const d = await json<PerfilVendedor>(query("vendedores/perfil", { id }), {
    signal,
  });
  if (d?.id !== id) throw new Error("No se pudo cargar tu perfil.");
  return d;
}
export async function guardarPerfil(id: string, datos: DatosPerfil) {
  const d = await json<PerfilVendedor>("vendedores/perfil", {
    method: "PATCH",
    body: JSON.stringify({ id, ...datos }),
  });
  if (d?.id !== id)
    throw new Error("No se recibió la confirmación del perfil.");
  return d;
}
// eliminadas: la papelera (solo admin; el backend ignora el pedido de un
// vendedor y devuelve su historial normal).
export async function historial(
  vendedorId: string,
  signal?: AbortSignal,
  eliminadas = false,
) {
  // n8n responde 200 con cuerpo vacío cuando el vendedor no tiene
  // cotizaciones (el nodo HTTP no emite items y nunca llega a responder):
  // eso es un historial vacío, no un error.
  const text = await (
    await request(
      query("cotizaciones-guardadas", {
        vendedor_id: vendedorId,
        eliminadas: eliminadas ? "1" : undefined,
      }),
      { signal },
    )
  ).text();
  if (!text.trim()) return [];
  let d: unknown;
  try {
    d = JSON.parse(text);
  } catch {
    throw new Error("El historial no está disponible.");
  }
  const rows = Array.isArray(d) ? d : registro(d).cotizaciones;
  if (!Array.isArray(rows)) throw new Error("El historial no está disponible.");
  // Con "Always Output Data" n8n puede mandar un item vacío ({}) en lugar
  // de una lista vacía: sin id no es una cotización.
  return rows.filter((r): r is Historial => typeof registro(r).id === "string");
}
// Borrar (lógico, recuperable por un admin) una cotización no entregada, y
// restaurarla. Ver POST /cotizaciones/:id/eliminar y /restaurar del backend.
export async function eliminarCotizacion(vendedor: Vendedor, id: string) {
  return post<{ id: string; numero: string; eliminada: boolean }>(
    "cotizaciones/eliminar",
    { id, vendedor_id: vendedor.id },
    20000,
  );
}
export async function restaurarCotizacionBorrada(
  vendedor: Vendedor,
  id: string,
) {
  return post<{ id: string; numero: string; eliminada: boolean }>(
    "cotizaciones/restaurar",
    { id, vendedor_id: vendedor.id },
    20000,
  );
}
export async function contactos(
  buscar: string,
  todos: boolean,
  signal?: AbortSignal,
) {
  const d = await json<Contacto[]>(
    query(todos ? "ghl-contactos/todos" : "ghl-contactos", { buscar }),
    { signal },
  );
  if (!Array.isArray(d))
    throw new Error("No se pudieron cargar los contactos.");
  return d;
}
export async function calcular(payload: unknown, signal?: AbortSignal) {
  const d = await json<ResultadoCalculo>(
    "cotizador-confirmar",
    { method: "POST", body: JSON.stringify(payload), signal },
    60000,
  );
  if (!Number.isFinite(d.total_general_interno) || !Array.isArray(d.sitios))
    throw new Error("El servicio no devolvió un cálculo válido.");
  return d;
}
// Vista previa: el MISMO PDF que genera n8n al entregar (HTML de marca →
// Gotenberg), pero forzado a canal "pdf" y sin contacto GHL — así
// /pdf/generar del backend no encola ninguna notificación y el workflow no
// envía nada. No guarda en el historial (eso lo hace solo `guardar`).
export async function pdfVistaPrevia(pdf: object, signal?: AbortSignal) {
  const response = await request(
    "cotizador-generar-pdf",
    {
      method: "POST",
      body: JSON.stringify({
        ...pdf,
        canal: "pdf",
        contacto_ghl: null,
        encargado_ghl_id: null,
      }),
      signal,
    },
    120000,
  );
  const blob = await response.blob();
  if (!blob.type.includes("pdf"))
    throw new Error("El servicio no devolvió un PDF.");
  return blob;
}
// Hoja de orden a producción (workflow n8n "Cotizador DV - Orden de
// producción"): n8n registra la orden en el backend (ordenes_produccion, una
// por cotización) y devuelve el PDF con su número OP-…; sin precios y sin
// contacto GHL, así que no notifica a nadie.
export async function ordenProduccionPdf(orden: object) {
  const response = await request(
    "cotizador-orden-produccion",
    { method: "POST", body: JSON.stringify(orden) },
    120000,
  );
  const blob = await response.blob();
  if (!blob.type.includes("pdf"))
    throw new Error("El servicio no devolvió la orden de producción.");
  return blob;
}
// Modo consulta del mismo workflow: la orden ya registrada (para prellenar el
// formulario) o null. No registra nada.
export async function ordenProduccionRegistrada(
  vendedorId: string,
  cotizacionId: string,
  signal?: AbortSignal,
) {
  const d = await json<{ orden?: OrdenRegistrada | null }>(
    "cotizador-orden-produccion",
    {
      method: "POST",
      body: JSON.stringify({
        consultar: true,
        vendedor_id: vendedorId,
        cotizacion_id: cotizacionId,
      }),
      signal,
    },
    30000,
  );
  if (!d || typeof d !== "object" || !("orden" in d))
    throw new Error("No se pudo consultar la orden de producción.");
  return d.orden ?? null;
}
export async function guardar(
  payload: unknown,
  vendedor: Vendedor,
  cliente: string,
  total: number,
) {
  const d = await post<{
    id: string;
    revision?: number;
    numero?: string;
    numero_base?: string;
    nueva_revision?: boolean;
  }>(
    "cotizaciones",
    {
      vendedor_id: vendedor.id,
      cliente_nombre: cliente,
      total_general: total,
      payload,
    },
    60000,
  );
  if (typeof d.id !== "string" || typeof d.numero !== "string")
    throw new Error(
      "No se recibió la confirmación de guardado. Verifica el historial antes de reintentar.",
    );
  return {
    id: d.id,
    revision: d.revision ?? null,
    numero: d.numero,
    numeroBase: d.numero_base ?? null,
    nuevaRevision: d.nueva_revision === true,
  };
}
// Pestaña "Cambiar estado" del manual: solo la etapa comercial, nunca el
// total (ese lo fija n8n al confirmar).
export async function cambiarEstado(
  vendedor: Vendedor,
  id: string,
  estado: string,
  nota: string,
) {
  const d = await json<{ estado?: string }>(
    "cotizaciones/estado",
    {
      method: "PATCH",
      body: JSON.stringify({ vendedor_id: vendedor.id, id, estado, nota }),
    },
    20000,
  );
  if (d.estado !== estado)
    throw new Error("No se confirmó el cambio de estado.");
  return d;
}
export async function subirImagen(imagen: string) {
  const d = await post<{ url: string }>(
    "cotizador-subir-imagen",
    { imagen_base64: imagen },
    60000,
  );
  if (!d.url) throw new Error("No se recibió la imagen guardada.");
  return d.url;
}
// registrada: quedó en la tabla Sugerencias de Airtable; notificada: salió
// el correo a Producción. El backend responde ok si al menos uno funcionó.
export async function sugerir(datos: unknown) {
  const d = await post<{
    ok: boolean;
    registrada?: boolean;
    notificada?: boolean;
  }>("sugerencias-producto", datos);
  if (d.ok !== true) throw new Error("La sugerencia no fue confirmada.");
  return {
    registrada: d.registrada === true,
    notificada: d.notificada === true,
  };
}
export async function comprimirImagen(file: File): Promise<string> {
  if (
    !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)
  )
    throw new Error("Selecciona una imagen JPG, PNG, WebP o GIF.");
  if (file.size > 20 * 1024 * 1024) throw new Error("La imagen supera 20 MB.");
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, 1600 / Math.max(img.width, img.height)),
          canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        canvas
          .getContext("2d")!
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = () => reject(new Error("No se pudo leer la imagen."));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---------- Catálogo: lectura de piezas por IA y alta de piezas ----------
// Las dos van por n8n al backend (cotizador-dv-backend, rutas /catalogo):
// la llave de Gemini y el token de Airtable nunca llegan al navegador.

export interface ItemExtraido {
  descripcion: string;
  // null = la IA leyó una cantidad imposible: el vendedor la escribe.
  cantidad: number | null;
  ancho: number | null;
  alto: number | null;
  profundidad: number | null;
  // Una unidad por medida: una imagen puede traer 2 m × 50 cm × 3 mm.
  unidad_ancho: "m" | "cm" | "mm";
  unidad_alto: "m" | "cm" | "mm";
  unidad_profundidad: "m" | "cm" | "mm";
  // Índice dentro de la lista `catalogo` enviada; -1 = no está en el catálogo.
  catalogo_indice: number;
  material: string;
  notas: string;
}
export async function extraerPiezasDeImagen(
  imagen: string,
  catalogo: {
    familia: string;
    variante: string;
    material: string;
    unidad: string;
  }[],
  vendedorId: string,
) {
  const d = await post<{ items?: ItemExtraido[]; truncado?: boolean }>(
    "cotizador-extraer-piezas",
    { vendedor_id: vendedorId, imagen_base64: imagen, catalogo },
    120000,
  );
  return {
    items: Array.isArray(d.items) ? d.items : [],
    // Había más ítems de los que se aceptan por imagen: hay que avisarlo.
    truncado: d.truncado === true,
  };
}
